import createClient, { type Middleware } from 'openapi-fetch';

import { deviceId } from '@/lib/device';
import { env } from '@/lib/env';

import { ApiError, SESSION_ENDED } from './errors';
import type { components, paths } from './schema';

export type StaffAuthenticated = components['schemas']['StaffAuthenticated'];
export type StaffProfile = components['schemas']['StaffProfile'];
type RefreshResult = components['schemas']['StaffRefreshResult'];

/* ----------------------------------------------------------------- estado del token */

/** El access token vive solo en memoria; el refresh, en cookie HttpOnly (`__Secure-csn_admin_rt`). */
let accessToken: string | null = null;
type Listener = (
  event: { type: 'token'; token: string | null } | { type: 'ended'; code: string },
) => void;
const listeners = new Set<Listener>();

export function onAuthEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function setAccessToken(token: string | null) {
  accessToken = token;
  listeners.forEach((l) => l({ type: 'token', token }));
}
function sessionEnded(code: string) {
  accessToken = null;
  listeners.forEach((l) => l({ type: 'ended', code }));
}

/* ---------------------------------------------------------------------- mantenimiento */

/** La API responde `503 MAINTENANCE` durante el corte M2. */
type MaintenanceListener = (info: { until?: string }) => void;
const maintenanceListeners = new Set<MaintenanceListener>();

export function onMaintenance(fn: MaintenanceListener): () => void {
  maintenanceListeners.add(fn);
  return () => maintenanceListeners.delete(fn);
}

/* -------------------------------------------------------------------------- envelope */

interface ErrorBody {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
  meta?: { requestId?: string };
}

export async function toApiError(res: Response): Promise<ApiError> {
  let body: ErrorBody = {};
  try {
    body = (await res.clone().json()) as ErrorBody;
  } catch {
    /* respuesta sin JSON (proxy, red) */
  }
  return new ApiError(
    res.status,
    body.error?.code ?? 'HTTP_ERROR',
    body.error?.message ?? 'Ocurrió un error inesperado. Inténtalo de nuevo en unos minutos.',
    body.error?.details ?? {},
    body.meta?.requestId,
  );
}

export const baseHeaders = () => ({
  'X-Client': 'admin',
  'X-Client-Version': '0.1.0',
  'X-Device-Id': deviceId(),
  'X-Requested-With': 'consalud-admin',
});

/* --------------------------------------------------------------------------- refresh */

let refreshing: Promise<RefreshResult | null> | null = null;

async function doRefresh(): Promise<RefreshResult | null> {
  for (let attempt = 0; attempt < 3; attempt++) {
    const res = await fetch(`${env.apiUrl}/api/v1/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: baseHeaders(),
    });
    if (res.ok) {
      const { data } = (await res.json()) as { data: RefreshResult };
      setAccessToken(data.accessToken);
      return data;
    }
    const err = await toApiError(res);
    // Otra pestaña acaba de rotar el refresh: la cookie nueva ya está en el navegador.
    if (err.code === 'REFRESH_CONFLICT') {
      await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
      continue;
    }
    if (res.status === 401) {
      if (err.code === 'SESSION_REVOKED') sessionEnded(err.code);
      return null;
    }
    throw err;
  }
  return null;
}

/**
 * Un solo refresh a la vez en todo el navegador: promesa compartida dentro de la pestaña y Web
 * Locks entre pestañas (si no, dos pestañas usarían el mismo refresh y la API lo vería como reuso).
 */
export function refreshSession(): Promise<RefreshResult | null> {
  refreshing ??= (async () => {
    try {
      if ('locks' in navigator) {
        return await navigator.locks.request('cs-admin-refresh', () => doRefresh());
      }
      return await doRefresh();
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/* ---------------------------------------------------------------------------- client */

const authMiddleware: Middleware = {
  onRequest({ request }) {
    Object.entries(baseHeaders()).forEach(([k, v]) => request.headers.set(k, v));
    if (accessToken && !request.headers.has('Authorization')) {
      request.headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return request;
  },
};

/** fetch con reintento tras renovar el token expirado (una sola vez por request). */
async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone();
  const res = await fetch(input);
  if (res.status === 503) {
    const err = await toApiError(res);
    if (err.code === 'MAINTENANCE') {
      const until = typeof err.details.until === 'string' ? err.details.until : undefined;
      maintenanceListeners.forEach((l) => l(until ? { until } : {}));
    }
    return res;
  }
  if (res.status !== 401 || !accessToken) return res;
  const err = await toApiError(res);
  if (err.code === 'TOKEN_EXPIRED') {
    const refreshed = await refreshSession();
    if (refreshed) {
      retry.headers.set('Authorization', `Bearer ${refreshed.accessToken}`);
      return fetch(retry);
    }
    sessionEnded('SESSION_EXPIRED');
    return res;
  }
  if (SESSION_ENDED.has(err.code)) sessionEnded(err.code);
  return res;
}

export const api = createClient<paths>({
  baseUrl: env.apiUrl,
  credentials: 'include',
  fetch: authFetch,
});
api.use(authMiddleware);

/** Desenvuelve `{ data }` o lanza `ApiError` (el mensaje viene en español desde la API). */
export async function unwrap<T>(
  promise: Promise<{ data?: { data: T }; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, response } = await promise;
  if (!response.ok || !data) throw await toApiError(response);
  return data.data;
}

/** Igual que `unwrap`, pero conserva `meta` (paginación). */
export async function unwrapPage<T>(
  promise: Promise<{
    data?: { data: T; meta: { page?: number; pageSize?: number; total?: number } };
    error?: unknown;
    response: Response;
  }>,
): Promise<{ data: T; page: number; pageSize: number; total: number }> {
  const { data, response } = await promise;
  if (!response.ok || !data) throw await toApiError(response);
  return {
    data: data.data,
    page: data.meta.page ?? 1,
    pageSize: data.meta.pageSize ?? 25,
    total: data.meta.total ?? 0,
  };
}

/** Respuestas 204 (sin cuerpo): solo lanza si falló. */
export async function ensureOk(promise: Promise<{ response: Response }>): Promise<void> {
  const { response } = await promise;
  if (!response.ok) throw await toApiError(response);
}
